/**
 * features/applications/ApplicationDocuments.tsx
 * ------------------------------------------------------------------
 * The office attaches an application's paperwork here.
 *
 * The documents were always required — the printed form has listed them
 * since it was built — and the office collected them by email or over a
 * counter. What was missing was anywhere to PUT them, so an application sat
 * while its paperwork lived in somebody's inbox, and the chase is where
 * documents got lost.
 *
 * THIS IS A GATE, NOT A FILING CABINET. An application cannot be printed
 * and cannot be approved until the four required documents are on file.
 * That is the only thing that makes "required" mean anything once the
 * requirement sits with a colleague rather than with the applicant — a
 * checklist nobody is stopped by is a checklist.
 *
 * THE APPLICANT DOES NOT UPLOAD. This briefly let a parent attach their own
 * files from the public form; the school said no, and they were right. It
 * would have been the only write path in the system not behind a role, and
 * the only way for somebody with no relationship to the school to put a
 * file in its bucket. Uploading here costs the office a step and costs the
 * school no exposure.
 *
 * DELETION IS DELIBERATE AND AUDITED. A wrong file attached to a child's
 * record has to be removable, but a document the office has already relied
 * on should not quietly vanish, so removing one is senior-only in
 * storage.rules and says who did it here.
 * ------------------------------------------------------------------ */
import { useState } from 'react';
import {
  Paperclip, CheckCircle2, AlertCircle, Download, Trash2, Loader2,
} from 'lucide-react';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage, updateDoc } from '@/lib/firebase';
import { doc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { auditFromContext } from '@/lib/audit';
import { SoftCard, PanelHeader } from '@/features/finance/ui';
import {
  DOCUMENT_REQUIREMENTS, ACCEPT_ATTRIBUTE, fileProblem, describeSize,
  documentsProblem, storagePathFor,
  type AttachedDocument, type DocumentSlot,
} from '@/lib/applicationDocuments';

export default function ApplicationDocuments({
  applicationId,
  applicantName,
  documents,
  canEdit,
  canDelete,
  userData,
}: {
  applicationId: string;
  applicantName: string;
  documents: AttachedDocument[];
  canEdit: boolean;
  canDelete: boolean;
  userData: { uid?: string; email?: string; displayName?: string; role?: string } | null;
}) {
  const [uploading, setUploading] = useState<DocumentSlot | null>(null);
  const [busyPath, setBusyPath] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const outstanding = documentsProblem(documents);

  async function persist(next: AttachedDocument[]) {
    await updateDoc(doc(db, 'reportApplications', applicationId), { documents: next });
  }

  async function attach(slot: DocumentSlot, file: File) {
    const problem = fileProblem(file, documents.length);
    if (problem) { setErr(problem); return; }
    setErr(null);
    setUploading(slot);
    try {
      const path = storagePathFor(applicationId, slot, file.name);
      await uploadBytes(storageRef(storage, path), file, { contentType: file.type || undefined });
      const record: AttachedDocument = {
        slot,
        fileName: file.name,
        storagePath: path,
        contentType: file.type || 'application/octet-stream',
        size: file.size,
        uploadedAt: new Date().toISOString(),
      };
      // One file per required slot: a second REPLACES the entry rather than
      // adding a duplicate somebody downstream has to choose between.
      const next = [
        ...documents.filter((d) => (slot === 'other' ? true : d.slot !== slot)),
        record,
      ];
      await persist(next);
      auditFromContext(userData, 'application_document_attached', 'admissions', {
        targetId: applicationId,
        targetType: 'application',
        targetName: applicantName,
        details: { slot, fileName: file.name, size: file.size },
      });
    } catch {
      setErr('That upload did not go through. Check the connection and try again.');
    } finally {
      setUploading(null);
    }
  }

  async function open(d: AttachedDocument) {
    setBusyPath(d.storagePath);
    try {
      window.open(await getDownloadURL(storageRef(storage, d.storagePath)), '_blank', 'noopener');
    } catch {
      setErr('That file could not be opened. It may have been removed.');
    } finally {
      setBusyPath(null);
    }
  }

  async function remove(d: AttachedDocument) {
    if (!window.confirm(`Remove ${d.fileName} from this application?`)) return;
    setBusyPath(d.storagePath);
    try {
      /* The record goes first. If the storage delete fails, the office sees
         a document that is gone rather than one that is listed and
         unopenable — and an orphaned file nobody references is litter,
         which is the cheaper of the two failures. */
      await persist(documents.filter((x) => x.storagePath !== d.storagePath));
      await deleteObject(storageRef(storage, d.storagePath)).catch(() => {});
      auditFromContext(userData, 'application_document_removed', 'admissions', {
        targetId: applicationId,
        targetType: 'application',
        targetName: applicantName,
        details: { slot: d.slot, fileName: d.fileName },
      });
    } catch {
      setErr('That file could not be removed.');
    } finally {
      setBusyPath(null);
    }
  }

  return (
    <SoftCard pad="p-0" hover={false}>
      <PanelHeader
        icon={Paperclip}
        title="Documents"
        subtitle="Attached by the office. Required before printing or approving."
        right={(
          <span className={`text-xs px-2 py-0.5 rounded-full ${outstanding
            ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
            : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'}`}
          >
            {outstanding ? 'Incomplete' : 'Complete'}
          </span>
        )}
      />

      <div className="p-4 space-y-3">
        {outstanding && (
          <p className="text-sm text-amber-700 dark:text-amber-300 flex items-start gap-1.5">
            <AlertCircle size={14} className="mt-0.5 shrink-0" />
            <span>
              {outstanding} The application cannot be printed or approved until these are on file.
            </span>
          </p>
        )}

        {err && (
          <p className="text-sm text-red-600 dark:text-red-400 flex items-start gap-1.5 rounded-lg bg-red-50 dark:bg-red-950/30 p-3">
            <AlertCircle size={14} className="mt-0.5 shrink-0" />
            <span>{err}</span>
          </p>
        )}

        {DOCUMENT_REQUIREMENTS.map((r) => {
          const held = documents.filter((d) => d.slot === r.slot);
          return (
            <div key={r.slot} className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-100">
                    {r.label}
                    {r.required && <span className="text-red-500"> *</span>}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{r.hint}</p>
                </div>
                {canEdit && (
                  <label className="shrink-0 cursor-pointer text-xs font-semibold px-2.5 py-1.5 rounded-lg border border-primaryLight text-primaryLight hover:bg-primaryLight hover:text-white">
                    {uploading === r.slot ? 'Uploading…' : held.length ? 'Replace' : 'Upload'}
                    <input
                      type="file"
                      className="hidden"
                      accept={ACCEPT_ATTRIBUTE}
                      disabled={uploading !== null}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        e.target.value = ''; // so the same file can be chosen twice
                        if (f) void attach(r.slot, f);
                      }}
                    />
                  </label>
                )}
              </div>

              {held.length > 0 && (
                <ul className="mt-2.5 space-y-1.5">
                  {held.map((d) => (
                    <li
                      key={d.storagePath}
                      className="flex items-center gap-2 text-sm bg-emerald-50 dark:bg-emerald-950/30 rounded-lg px-3 py-2"
                    >
                      <CheckCircle2 size={14} className="text-emerald-600 shrink-0" />
                      <span className="truncate text-gray-800 dark:text-gray-100">{d.fileName}</span>
                      <span className="text-xs text-gray-500 ml-auto shrink-0">{describeSize(d.size)}</span>
                      <button
                        type="button"
                        onClick={() => { void open(d); }}
                        disabled={busyPath === d.storagePath}
                        className="text-gray-400 hover:text-primaryLight shrink-0"
                        title="Open"
                      >
                        {busyPath === d.storagePath
                          ? <Loader2 size={14} className="animate-spin" />
                          : <Download size={14} />}
                      </button>
                      {canDelete && (
                        <button
                          type="button"
                          onClick={() => { void remove(d); }}
                          disabled={busyPath === d.storagePath}
                          className="text-gray-400 hover:text-red-600 shrink-0"
                          title="Remove"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </SoftCard>
  );
}
